import { useEffect } from "react";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { CharacterCount } from "@tiptap/extensions";
import { countWords } from "../../utils";
import type { UseTipTapEditorOptions } from "./types";
import {
  isValidHref,
  normaliseHref,
  sanitiseText,
} from "../../../formatting/sanitise-rich-text";
import { tipTapLinkProtocols } from "../../../formatting/defaults";

export function createConfig() {
  return [
    StarterKit.configure({
      blockquote: false,
      bold: {},
      bulletList: {},
      code: false,
      codeBlock: false,
      heading: false,
      horizontalRule: false,
      italic: {},
      link: false,
      listItem: {},
      orderedList: {},
      strike: false,
    }),
    CharacterCount.configure({ wordCounter: countWords }),
    Link.configure({
      autolink: true,
      linkOnPaste: true,
      openOnClick: false,
      protocols: tipTapLinkProtocols,
      HTMLAttributes: {
        rel: "noopener",
        target: "_blank",
      },
      isAllowedUri: (url, context) =>
        context.defaultValidate(url) && isValidHref(normaliseHref(url)),
    }),
  ];
}

const narrativeEditorExtensions = createConfig();

function useTipTapEditor({
  id,
  label,
  initialValue,
  onBlur,
  onUpdate,
  disabled = false,
}: UseTipTapEditorOptions) {
  const editor = useEditor({
    editable: !disabled,
    extensions: narrativeEditorExtensions,
    content: initialValue,
    shouldRerenderOnTransaction: false,
    editorProps: {
      attributes: {
        id,
        role: "textbox",
        "aria-label": label,
        "aria-multiline": "true",
      },
      transformPastedHTML: sanitiseText,
    },
    onUpdate: ({ editor: updatedEditor }) =>
      onUpdate?.(
        updatedEditor.getHTML(),
        updatedEditor.storage.characterCount.words(),
      ),
    onBlur: ({ editor: blurredEditor }) =>
      onBlur?.(
        blurredEditor.getHTML(),
        blurredEditor.storage.characterCount.words(),
      ),
  });

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  return editor;
}

export default useTipTapEditor;
